import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Dashboard from './pages/dashboard/Dashboard';
import LicenseList from './pages/licenses/LicenseList';
import LicenseDetail from './pages/licenses/LicenseDetail';
import CreateLicense from './pages/licenses/CreateLicense';
import ApplicationList from './pages/applications/ApplicationList';
import CreateApplication from './pages/applications/CreateApplication';
import ApplicationDetail from './pages/applications/ApplicationDetail';
import EditApplication from './pages/applications/EditApplication';
import SystemSettings from './pages/settings/SystemSettings';
import BrandSettings from './pages/settings/BrandSettings';
import UserManagement from './pages/users/UserManagement';
import Profile from './pages/profile/Profile';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminRoute from './components/common/AdminRoute';
import PermRoute from './components/common/PermRoute';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import ApiDocumentation from './pages/docs/ApiDocumentation';
import ApplicationRequests from './pages/licenses/ApplicationRequests';
import InstanceList from './pages/monitor/InstanceList';
import InstanceDetail from './pages/monitor/InstanceDetail';
import CommandCenter from './pages/commands/CommandCenter';
import CommandHistory from './pages/commands/CommandHistory';
import SignalList from './pages/signals/SignalList';
import SourceManager from './pages/signals/SourceManager';
import Statistics from './pages/statistics/Statistics';
import SecurityAudit from './pages/security/SecurityAudit';

const App = () => {
  const location = useLocation();

  // 添加滚动效果
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <AuthProvider>
      <SettingsProvider>
        <div className="min-h-screen bg-[#f6f8fa] text-[#1f2328]">
          <Routes>
            {/* 公开路由 */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* 受保护路由 */}
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* 平台总览(需权限) */}
              <Route element={<PermRoute perm="dashboard" />}>
                <Route path="/dashboard" element={<Dashboard />} />
              </Route>

              {/* 授权管理板块(需权限) */}
              <Route element={<PermRoute perm="licenses" />}>
                <Route path="/applications" element={<ApplicationList />} />
                <Route path="/applications/create" element={<CreateApplication />} />
                <Route path="/applications/:id" element={<ApplicationDetail />} />
                <Route path="/applications/:id/edit" element={<EditApplication />} />
                <Route path="/licenses/applications" element={<ApplicationRequests />} />
                <Route path="/licenses" element={<LicenseList />} />
                <Route path="/licenses/create" element={<CreateLicense />} />
                <Route path="/licenses/:id" element={<LicenseDetail />} />
              </Route>

              {/* 实时监控板块(需权限) */}
              <Route element={<PermRoute perm="monitor" />}>
                <Route path="/monitor/instances" element={<InstanceList />} />
                <Route path="/monitor/instances/:instanceId" element={<InstanceDetail />} />
              </Route>

              {/* 远程指令板块(需权限) */}
              <Route element={<PermRoute perm="commands" />}>
                <Route path="/commands" element={<CommandCenter />} />
                <Route path="/commands/history" element={<CommandHistory />} />
              </Route>

              {/* 云端下发板块(需权限, 超管开通) */}
              <Route element={<PermRoute perm="signals" />}>
                <Route path="/signals" element={<SignalList />} />
                <Route path="/signal-sources" element={<SourceManager />} />
              </Route>

              {/* 数据统计板块(需权限) */}
              <Route element={<PermRoute perm="statistics" />}>
                <Route path="/statistics" element={<Statistics />} />
              </Route>

              {/* 个人资料 */}
              <Route path="/profile" element={<Profile />} />
              {/* API 文档(随系统管理权限) */}
              <Route element={<PermRoute perm="system" />}>
                <Route path="/api-docs" element={<ApiDocumentation />} />
              </Route>

              {/* 仅管理员可访问的路由 */}
              <Route element={<AdminRoute />}>
                <Route path="/users" element={<UserManagement />} />
                <Route path="/security" element={<SecurityAudit />} />
              </Route>

              {/* 系统设置: 仅超管(代理/VIP的贴牌入口在侧边栏左下角"页面风格") */}
              <Route element={<PermRoute perm="system" />}>
                <Route path="/settings" element={<SystemSettings />} />
              </Route>

              {/* 贴牌设置: VIP权益(自定义品牌标题/背景图), 页面内按角色分流 */}
              <Route path="/brand" element={<BrandSettings />} />
            </Route>

            {/* 404页面 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </SettingsProvider>
    </AuthProvider>
  );
};

export default App;
